import { useQuery } from '@apollo/client/react';

import FilesTab from '@atom/components/common/filesTab/FilesTab';
import { GET_USER } from '@atom/graph/user';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Progress } from '@atom/mui';
import { SubjectType } from '@atom/types/media';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import BudgetsTab from './budgetsTab/BudgetsTab';
import CertificationsTab from './certificationsTab/CertificationsTab';
import GroupsTab from './groupsTab/GroupsTab';
import UserHeader from './header/UserHeader';
import InfoTab from './infoTab/InfoTab';
import PermissionsTab from './permissionsTab/PermissionsTab';
import RolesTab from './rolesTab/RolesTab';
import SettingsTab from './settingsTab/SettingsTab';
import WorkTab from './workTab/WorkTab';
import UserContext, { View } from './UserContext';

import classes from './userDetail.module.css';

const UserDetail = () => {
  const [{ view: viewParam }, { params, updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
  });
  const { id } = params;

  const activeView = viewParam || View.INFO;

  const setActiveView = (view: View) => {
    updateSearchParams({ view });
  };

  const { loading, data, error, refetch } = useQuery(GET_USER, {
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
  });

  if (error || !data) {
    return <Progress style={{ height: '100%' }} />;
  }

  const getActiveView = () => {
    const views = {
      [View.INFO]: <InfoTab />,
      [View.GROUPS]: <GroupsTab />,
      [View.BUDGETS]: <BudgetsTab />,
      [View.PERMISSIONS]: <PermissionsTab />,
      [View.CERTIFICATIONS]: <CertificationsTab />,
      [View.WORK]: <WorkTab />,
      [View.SETTINGS]: <SettingsTab />,
      [View.ROLES]: <RolesTab />,
      [View.FILES]: (
        <FilesTab
          subjectId={data?.user?.id}
          subjectType={SubjectType.USER}
          canCreate={hasRolePermissions(ROLE_SETS.ORG_ADMIN)}
          canUpdate={hasRolePermissions(ROLE_SETS.ORG_ADMIN)}
          canDelete={hasRolePermissions(ROLE_SETS.ORG_ADMIN)}
        />
      ),
    };

    return views[activeView];
  };

  const contextValue = {
    activeView,
    setActiveView,
    user: data.user,
    loading,
    refetch,
  };

  return (
    <UserContext.Provider value={contextValue}>
      <div className={classes.userDetailContainer}>
        <UserHeader />
        <div className={classes.contentContainer}>
          {activeView === View.INFO ||
          activeView === View.WORK ||
          activeView === View.FILES ? (
            <div className={classes.fullPaneContainer}>{getActiveView()}</div>
          ) : (
            <div className={classes.centerPaneContainer}>
              <div className={classes.centerPane} style={{ overflow: 'auto' }}>
                {getActiveView()}
              </div>
            </div>
          )}
        </div>
      </div>
    </UserContext.Provider>
  );
};

export default UserDetail;
